import React, { useMemo } from "react";
import styled from "styled-components";
import { Row, Col } from "react-bootstrap";

import { FORM_FIELD_KEYS } from "../../../../../constants/forms";
import { getFieldProps } from "../../../../../utils/form-fields";
import { hexToRGBA } from "../../../../../utils/theme";

import TextInput from "../../../controls/TextInput";
import Checkbox from "../../../controls/Switch";
import Select from "../../../controls/Select";
import { isStateLimited, state_array as STATES } from "../../../../../../variables/Variables";

const StyledStateDesc = styled.div`
  font-family: 'InterRegular';
  font-style: italic;
  font-size: 12px;
  margin-bottom: 16px;
`;

const StyledContainer = styled.div`
  margin-bottom: 24px;
  border: 1px solid ${hexToRGBA("#000000", 0.1)};
`;

const StyledRow = styled(Row)`
  padding: 16px;
`;

function BusinessAddressBlock({ prefix = "corp", values = {}, onChange, onMouseEnter, onMouseLeave }) {
  const showAltAddress = values[FORM_FIELD_KEYS.altAddress] === "true";

  const title = useMemo(() => {
    let titleText = "Business Address (PO Boxes are not authorized)";

    if (values.code === "EIN_Estate") {
      titleText = "Executor/Legal Personal Representative Address (PO Boxes are not authorized)";
    }
    if (values.code === "EIN_Trust") {
      titleText = "Trustee Address (PO Boxes are not authorized)";
    }

    return titleText;
  }, [values]);

  const addressHint = useMemo(() => {
    let firstPart = "Please enter the physical address of the business/entity.";
    let secondPart = "Business AddressNo P.O. Boxes Allowed";

    if (values.code === "EIN_Estate") {
      firstPart = "Enter the street address of the person that will manage the Estate.";
    }
    if (values.code === "EIN_Trust") {
      firstPart = "Please enter the physical address of the Trustee.";
    }
    if (values.code === "EIN_Church") {
      firstPart = "Please enter the physical address of the Church.";
    }
    if (values.code === "EIN_NonProfit") {
      firstPart = "Please enter the physical address of the Non Profit.";
    }

    return `${firstPart} ${secondPart}`;
  }, [values]);

  const countyHint = useMemo(() => {
    let firstPart = "Enter the county of the business/entity.";
    let secondPart = "Do not confuse “county” with “country”.";

    if (values.code === "EIN_Estate") {
      firstPart = "Enter the county of the person that will manage the Estate.";
    }
    if (values.code === "EIN_Trust") {
      firstPart = "Enter the county of the Trustee.";
    }
    if (values.code === "EIN_Church") {
      firstPart = "Enter the county of the Church.";
    }
    if (values.code === "EIN_NonProfit") {
      firstPart = "Enter the county of the Non Profit.";
    }

    return `${firstPart} ${secondPart}`;
  }, [values]);

  return (
    <StyledContainer>
      <div className="inputs-block-title">
        <span>{title}</span>
      </div>
      <StyledRow>
        <Col xs={12}>
          <TextInput
            {...getFieldProps(FORM_FIELD_KEYS.baseAddressAddress1.replace("base", prefix))}
            label={getFieldProps(FORM_FIELD_KEYS.baseAddressAddress1.replace("base", prefix))?.placeholder?.replace(
              "*",
              ""
            )}
            placeholder={undefined}
            isCorpAddress
            hint={addressHint}
            pattern={`(?!.*(?:[pP][oO] [bB][oO][xX]))^[ a-zA-Z0-9\\\\\\/\\-]+$`}
            defaultInvalidMessage="The only special characters allowed in the Street field are - and /. PO Boxes are not acceptable."
            value={values[FORM_FIELD_KEYS.baseAddressAddress1.replace("base", prefix)]}
            onChange={onChange}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          />
        </Col>
        <Col xs={12}>
          <TextInput
            {...getFieldProps(FORM_FIELD_KEYS.baseAddressAddress2.replace("base", prefix))}
            label={getFieldProps(FORM_FIELD_KEYS.baseAddressAddress2.replace("base", prefix))?.placeholder?.replace(
              "*",
              ""
            )}
            placeholder={undefined}
            isCorpAddress
            pattern={`(?!.*(?:[pP][oO] [bB][oO][xX]))^[ a-zA-Z0-9\/\s\-]+$`}
            defaultInvalidMessage="The only special characters allowed in the Street field are - and /. PO Boxes are not acceptable."
            value={values[FORM_FIELD_KEYS.baseAddressAddress2.replace("base", prefix)]}
            onChange={onChange}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          />
        </Col>
        <Col xs={12}>
          <TextInput
            {...getFieldProps(FORM_FIELD_KEYS.baseAddressCity.replace("base", prefix))}
            label={getFieldProps(FORM_FIELD_KEYS.baseAddressCity.replace("base", prefix))?.placeholder?.replace(
              "*",
              ""
            )}
            placeholder={undefined}
            value={values[FORM_FIELD_KEYS.baseAddressCity.replace("base", prefix)]}
            onChange={onChange}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          />
        </Col>
        <Col xs={12}>
          <TextInput
            {...getFieldProps(FORM_FIELD_KEYS.baseAddressZip.replace("base", prefix))}
            label={getFieldProps(FORM_FIELD_KEYS.baseAddressZip.replace("base", prefix))?.placeholder?.replace("*", "")}
            placeholder={undefined}
            value={values[FORM_FIELD_KEYS.baseAddressZip.replace("base", prefix)]}
            onChange={onChange}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          />
        </Col>
        <Col xs={12}>
          {values.code==="EIN_LLC" && isStateLimited(values?.organizedState) ? (
            <>
              <Select
                {...getFieldProps(FORM_FIELD_KEYS.baseAddressState.replace("base", prefix))}
                label="State / Province"
                value={values[FORM_FIELD_KEYS.baseAddressState.replace("base", prefix)]}
                validated={values.validated}
                onChange={onChange}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                disabled
                noBottomSpace
              />
              <StyledStateDesc>
                For {STATES[values?.organizedState] || 'this state'} filings, the
                physical address must be located within the state.
              </StyledStateDesc>
            </>) : (
          <Select
            {...getFieldProps(FORM_FIELD_KEYS.baseAddressState.replace("base", prefix))}
            label="State / Province"
            value={values[FORM_FIELD_KEYS.baseAddressState.replace("base", prefix)]}
            validated={values.validated}
            onChange={onChange}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          />)}
        </Col>
        <Col xs={12}>
          <TextInput
            {...getFieldProps(FORM_FIELD_KEYS.baseAddressCounty.replace("base", prefix))}
            label={getFieldProps(FORM_FIELD_KEYS.baseAddressCounty.replace("base", prefix))?.placeholder?.replace(
              "*",
              ""
            )}
            placeholder={undefined}
            hint={countyHint}
            value={values[FORM_FIELD_KEYS.baseAddressCounty.replace("base", prefix)]}
            onChange={onChange}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          />
        </Col>
        <Col xs={12}>
          <Checkbox
            {...getFieldProps(FORM_FIELD_KEYS.altAddress)}
            value={values[FORM_FIELD_KEYS.altAddress]}
            onChange={onChange}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            hideDivider={!showAltAddress}
          />
        </Col>

        {showAltAddress && (
          <>
            <Col xs={12}>
              <TextInput
                {...getFieldProps(FORM_FIELD_KEYS.altAddressAddress1)}
                label={getFieldProps(FORM_FIELD_KEYS.altAddressAddress1)?.placeholder?.replace("*", "")}
                placeholder={undefined}
                pattern="^[ a-zA-Z0-9\\/-]+$"
                defaultInvalidMessage="The only special characters allowed in the Street field are - and /"
                value={values[FORM_FIELD_KEYS.altAddressAddress1]}
                onChange={onChange}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
              />
            </Col>
            <Col xs={12}>
              <TextInput
                {...getFieldProps(FORM_FIELD_KEYS.altAddressAddress2)}
                label={getFieldProps(FORM_FIELD_KEYS.altAddressAddress2)?.placeholder?.replace("*", "")}
                placeholder={undefined}
                pattern="^[ a-zA-Z0-9\\/-]+$"
                defaultInvalidMessage="The only special characters allowed in the Street field are - and /"
                value={values[FORM_FIELD_KEYS.altAddressAddress2]}
                onChange={onChange}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
              />
            </Col>
            <Col xs={12}>
              <TextInput
                {...getFieldProps(FORM_FIELD_KEYS.altAddressCity)}
                label={getFieldProps(FORM_FIELD_KEYS.altAddressCity)?.placeholder?.replace("*", "")}
                placeholder={undefined}
                value={values[FORM_FIELD_KEYS.altAddressCity]}
                onChange={onChange}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
              />
            </Col>
            <Col xs={12}>
              <TextInput
                {...getFieldProps(FORM_FIELD_KEYS.altAddressZip)}
                label={getFieldProps(FORM_FIELD_KEYS.altAddressZip)?.placeholder?.replace("*", "")}
                placeholder={undefined}
                value={values[FORM_FIELD_KEYS.altAddressZip]}
                onChange={onChange}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
              />
            </Col>
            <Col xs={12}>
              <Select
                {...getFieldProps(FORM_FIELD_KEYS.altAddressState)}
                label={getFieldProps(FORM_FIELD_KEYS.altAddressState)?.placeholder?.replace("*", "")}
                value={values[FORM_FIELD_KEYS.altAddressState]}
                validated={values.validated}
                onChange={onChange}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
              />
            </Col>
            <Col xs={12}>
              <TextInput
                {...getFieldProps(FORM_FIELD_KEYS.altAddressCounty)}
                label={getFieldProps(FORM_FIELD_KEYS.altAddressCounty)?.placeholder?.replace("*", "")}
                placeholder={undefined}
                hint={countyHint}
                value={values[FORM_FIELD_KEYS.altAddressCounty]}
                onChange={onChange}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                hideDivider
              />
            </Col>
          </>
        )}
      </StyledRow>
    </StyledContainer>
  );
}

export default BusinessAddressBlock;
